import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import { Sparkles, Loader2, Save, Languages, AlertCircle } from 'lucide-react';
import { LANGUAGES } from '../../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NON_EN_LANGS = LANGUAGES.filter(l => l.code !== 'en');

const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * FormTranslationsTab
 *
 * Author-facing UI for managing the multilingual labels of a saved form.
 * Behavior:
 *  - "Auto-translate with AI" hits POST /api/forms/:id/translate which uses
 *    the Emergent LLM key (Claude Sonnet 4.5) to translate every field label,
 *    placeholder, info_text, and option array into the 6 non-English locales.
 *  - The author can then refine any field's translation per language; saving
 *    persists via PUT /api/forms/:id/translations/:lang.
 *
 * Source-of-truth language is English — this component reads the English
 * label of each field from `fields` (the live form-builder state) so the
 * author sees what they're translating from.
 */
export default function FormTranslationsTab({ formId, formName, formDescription, fields }) {
  const [translations, setTranslations] = useState({});
  const [busyLang, setBusyLang] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [activeLang, setActiveLang] = useState('es');

  const load = async () => {
    if (!formId) return;
    try {
      const r = await axios.get(`${API_URL}/api/forms/${formId}`, { headers: authHeader() });
      setTranslations(r.data?.translations || {});
    } catch (err) {
      // non-fatal — empty translations are valid
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [formId]);

  if (!formId) {
    return (
      <Card className="careiq-card" data-testid="translations-needs-save">
        <CardContent className="p-6 text-center text-sm text-[#64748B]">
          <AlertCircle className="w-6 h-6 text-[#94A3B8] mx-auto mb-2" />
          Save the form first to manage translations.
        </CardContent>
      </Card>
    );
  }

  const generateAll = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API_URL}/api/forms/${formId}/translate`, {},
        { headers: authHeader() });
      setTranslations(r.data?.translations || {});
      toast.success('Translations generated. You can edit them below.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Translation failed');
    } finally {
      setGenerating(false);
    }
  };

  const updateField = (lang, fieldId, key, value) => {
    setTranslations(prev => {
      const copy = { ...prev };
      const cur = copy[lang] ? { ...copy[lang] } : {};
      const curFields = cur.fields ? { ...cur.fields } : {};
      curFields[fieldId] = { ...(curFields[fieldId] || {}), [key]: value };
      cur.fields = curFields;
      copy[lang] = cur;
      return copy;
    });
  };

  const updateOptions = (lang, fieldId, idx, value) => {
    setTranslations(prev => {
      const copy = { ...prev };
      const cur = copy[lang] ? { ...copy[lang] } : {};
      const curFields = cur.fields ? { ...cur.fields } : {};
      const f = curFields[fieldId] ? { ...curFields[fieldId] } : {};
      const opts = Array.isArray(f.options) ? [...f.options] : [];
      opts[idx] = value;
      f.options = opts;
      curFields[fieldId] = f;
      cur.fields = curFields;
      copy[lang] = cur;
      return copy;
    });
  };

  const updateTop = (lang, key, value) => {
    setTranslations(prev => ({ ...prev, [lang]: { ...(prev[lang] || {}), [key]: value } }));
  };

  const saveLang = async (lang) => {
    setBusyLang(lang);
    try {
      const payload = {
        name: translations[lang]?.name || '',
        description: translations[lang]?.description || '',
        fields: translations[lang]?.fields || {},
      };
      await axios.put(`${API_URL}/api/forms/${formId}/translations/${lang}`, payload,
        { headers: authHeader() });
      toast.success(`Saved ${LANGUAGES.find(l => l.code === lang)?.label} translations`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setBusyLang(null);
    }
  };

  const visibleFields = (fields || []).filter(f =>
    f.field_type !== 'section' && (f.label || f.options || f.placeholder || f.info_text)
  );

  return (
    <div className="space-y-4" data-testid="form-translations-tab">
      <Card className="careiq-card">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-[#ECFEFF] flex items-center justify-center shrink-0">
              <Languages className="w-5 h-5 text-[#0E7490]" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#0A2342]">Multilingual form labels</div>
              <div className="text-xs text-[#64748B]">
                Click <strong>Auto-translate</strong> to generate translations for all 6 non-English
                languages, then refine per field. English remains the source of truth.
              </div>
            </div>
          </div>
          <Button
            onClick={generateAll}
            disabled={generating}
            className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"
            data-testid="generate-translations-btn"
          >
            {generating
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Translating…</>
              : <><Sparkles className="w-4 h-4 mr-1" /> Auto-translate with AI</>}
          </Button>
        </CardContent>
      </Card>

      <Tabs value={activeLang} onValueChange={setActiveLang}>
        <TabsList className="flex flex-wrap gap-1 bg-[#F1F5F9] p-1">
          {NON_EN_LANGS.map(l => (
            <TabsTrigger
              key={l.code}
              value={l.code}
              data-testid={`tx-tab-${l.code}`}
              className="text-xs flex items-center gap-1"
            >
              <span className="text-sm">{l.flag}</span>
              <span>{l.native}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {NON_EN_LANGS.map(l => {
          const tx = translations[l.code] || {};
          const fmap = tx.fields || {};
          return (
            <TabsContent key={l.code} value={l.code} className="mt-3 space-y-3" dir={l.rtl ? 'rtl' : 'ltr'}>
              <Card className="careiq-card">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-[#0A2342] flex items-center gap-2">
                    <span className="text-base">{l.flag}</span> {l.label} ({l.native})
                  </CardTitle>
                  <Button size="sm" onClick={() => saveLang(l.code)} disabled={busyLang === l.code}
                          className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"
                          data-testid={`save-tx-${l.code}`}>
                    {busyLang === l.code
                      ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</>
                      : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">English</div>
                      <div className="text-sm text-[#0A2342]">{formName || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Translated form name</div>
                      <Input value={tx.name || ''} onChange={(e) => updateTop(l.code, 'name', e.target.value)}
                             placeholder="Translated name" data-testid={`tx-name-${l.code}`} />
                    </div>
                  </div>
                  {formDescription && (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">English description</div>
                        <div className="text-xs text-[#64748B] whitespace-pre-wrap">{formDescription}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Translated description</div>
                        <Textarea value={tx.description || ''} onChange={(e) => updateTop(l.code, 'description', e.target.value)}
                                  rows={2} data-testid={`tx-desc-${l.code}`} />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {visibleFields.map((f, idx) => {
                const t = fmap[f.field_id] || {};
                return (
                  <Card key={f.field_id} className="careiq-card" data-testid={`tx-field-${l.code}-${idx}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="text-[10px] uppercase tracking-wide text-[#94A3B8]">
                        Field · {f.field_type}
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">English label</div>
                          <div className="text-sm text-[#0A2342] font-medium">{f.label || '—'}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Translated label</div>
                          <Input value={t.label || ''} onChange={(e) => updateField(l.code, f.field_id, 'label', e.target.value)}
                                 placeholder="Label translation" />
                        </div>
                      </div>
                      {f.placeholder && (
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">English placeholder</div>
                            <div className="text-xs text-[#64748B]">{f.placeholder}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Translated placeholder</div>
                            <Input value={t.placeholder || ''} onChange={(e) => updateField(l.code, f.field_id, 'placeholder', e.target.value)} />
                          </div>
                        </div>
                      )}
                      {f.info_text && (
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">English info</div>
                            <div className="text-xs text-[#64748B] whitespace-pre-wrap">{f.info_text}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Translated info</div>
                            <Textarea value={t.info_text || ''} onChange={(e) => updateField(l.code, f.field_id, 'info_text', e.target.value)} rows={2} />
                          </div>
                        </div>
                      )}
                      {Array.isArray(f.options) && f.options.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Options</div>
                          <div className="space-y-2">
                            {f.options.map((opt, i) => (
                              <div key={i} className="grid sm:grid-cols-2 gap-2">
                                <div className="text-sm text-[#64748B] bg-[#F8FAFC] px-2 py-1.5 rounded border border-[#E2E8F0]">{opt}</div>
                                <Input value={(t.options && t.options[i]) || ''}
                                       onChange={(e) => updateOptions(l.code, f.field_id, i, e.target.value)}
                                       placeholder="Translated option" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {visibleFields.length === 0 && (
                <Card className="careiq-card"><CardContent className="p-6 text-center text-sm text-[#64748B]">
                  No translatable fields yet — add a few fields first.
                </CardContent></Card>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
